import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePanelesFlotantesStore } from '@/shared/overlay/estadoPanelesFlotantes';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Contenido adicional que se muestra sobre el footer principal
   * Útil para mensajes de estado, avisos o información contextual
   */
  footerTop?: React.ReactNode;
  headerActions?: React.ReactNode;
  tabBar?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  overlayClassName?: string;
  /**
   * Si es `false`, este Drawer no cuenta como "panel abierto" para efectos como
   * ocultar el tour guiado. Úsalo únicamente cuando el propio contenido de este
   * Drawer es el que aloja al tour (ej. el formulario de Nuevo Cliente); para
   * cualquier otro caso debe quedar en su valor por defecto (`true`).
   * @default true
   */
  contarComoPanelAbierto?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',      // 448px - Formularios simples
  md: 'max-w-xl',      // 576px - Formularios medianos
  lg: 'max-w-2xl',     // 672px - Formularios complejos (default)
  xl: 'max-w-4xl',     // 896px - Formularios muy extensos
  full: 'max-w-full',  // 100% - Casos especiales
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  footerTop,
  headerActions,
  tabBar,
  size = 'lg',
  className = '',
  overlayClassName = '',
  contarComoPanelAbierto = true,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const abrirPanel = usePanelesFlotantesStore((s) => s.abrirPanel);
  const cerrarPanel = usePanelesFlotantesStore((s) => s.cerrarPanel);

  // Mientras el drawer está montado (incluida su animación de cierre), cuenta
  // como "panel abierto" para que otros elementos (ej. el tour guiado) se oculten.
  // `contarComoPanelAbierto=false` excluye a este Drawer de esa cuenta (ver doc del prop).
  useEffect(() => {
    if (!shouldRender || !contarComoPanelAbierto) return;
    abrirPanel();
    return () => cerrarPanel();
  }, [shouldRender, contarComoPanelAbierto, abrirPanel, cerrarPanel]);

  // Manejar montaje/desmontaje con animación
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Pequeño delay para que el navegador aplique las transiciones
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Esperar a que termine la animación antes de desmontar
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Bloquear scroll del body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      firstElement?.focus();
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 dark:bg-black/60 transition-opacity duration-300 z-[60] ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        } ${overlayClassName}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        className={`
          fixed top-0 right-0 h-full w-full ${sizeClasses[size]}
          bg-white dark:bg-gray-900
          shadow-2xl
          transform transition-transform duration-300 ease-in-out
          z-[70] flex flex-col
          ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }
          ${className}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2
            id="drawer-title"
            className="text-h4 font-display text-primary"
          >
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {headerActions}
            <button type="button"
              onClick={onClose}
              className="
                p-1.5 -mr-1.5
                text-gray-400 hover:text-gray-600 
                dark:text-gray-500 dark:hover:text-gray-300
                rounded-lg
                hover:bg-gray-100 dark:hover:bg-gray-800
                transition-colors
                focus:outline-none focus:ring-2 focus:ring-primary-500
              "
              aria-label="Cerrar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* TabBar (opcional, sticky) */}
        {tabBar && (
          <div className="sticky top-0 z-10 bg-surface-0 border-b border-default flex-shrink-0 px-6">
            {tabBar}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {children}
        </div>

        {/* Footer Top (opcional) - Sección superior del footer */}
        {footerTop && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
            {footerTop}
          </div>
        )}

        {/* Footer (opcional) - Sección principal con acciones */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};



